import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, CalendarX, CircleAlert, ClipboardCheck, MessageCircle, ShieldAlert, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/motion";

const Block = ({ icon: Icon, title, children }) => (
  <Reveal>
    <section className="glass p-7 h-full">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 border border-gold/30 flex items-center justify-center"><Icon size={16} className="text-gold" /></div>
        <h2 className="font-display text-xl">{title}</h2>
      </div>
      <div className="text-luxury-body text-sm space-y-3">{children}</div>
    </section>
  </Reveal>
);

export default function CancellationPolicyPage() {
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    api.get("/rules").then((r) => { setData(r.data); setState("ready"); }).catch(() => setState("error"));
  }, []);

  if (state === "loading") {
    return <div className="pt-32 pb-24 min-h-screen" data-testid="cancellation-page" aria-busy="true"><div className="max-w-4xl mx-auto px-6 space-y-6">{[...Array(3)].map((_, i) => <div key={i} className="glass h-40 animate-pulse" />)}</div></div>;
  }
  if (state === "error" || !data) {
    return (
      <div className="pt-40 pb-24 min-h-screen text-center" data-testid="cancellation-page">
        <CircleAlert className="mx-auto text-gold mb-4" />
        <p className="text-white/60">We couldn't load the cancellation policy right now. Please try again shortly.</p>
      </div>
    );
  }

  const c = data.cancellation;
  const nonRefundable = c.refund_type === "NON_REFUNDABLE";
  const t = data.timings;

  return (
    <div className="pt-32 pb-24 grain min-h-screen" data-testid="cancellation-page">
      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-12">
          <div className="eyebrow">Policy</div>
          <h1 className="font-display text-5xl sm:text-6xl mt-6">Cancellation Policy</h1>
          <div className="divider-gold mx-auto" />
          <p className="text-luxury-body max-w-xl mx-auto">How cancellations and refunds work at {data.name}. Please read this before you book.</p>
        </div>

        <Reveal>
          <div className={`p-8 border text-center mb-8 ${nonRefundable ? "border-red-500/40 bg-red-500/[0.05]" : "border-gold/25 glass"}`} data-testid="policy-headline">
            <div className={`mx-auto w-14 h-14 border flex items-center justify-center mb-4 ${nonRefundable ? "border-red-500/50" : "border-gold/40"}`}>
              {nonRefundable ? <Ban size={24} className="text-red-400" /> : <ClipboardCheck size={24} className="text-gold" />}
            </div>
            <div className="font-display text-3xl">{nonRefundable ? "All bookings are non-refundable" : c.name}</div>
            <p className="text-white/70 mt-3 max-w-xl mx-auto">
              {nonRefundable ? c.non_refundable_notice : c.description}
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6">
          {nonRefundable ? (
            <>
              <Block icon={Ban} title="What non-refundable means">
                <ul className="list-disc pl-5 space-y-2">
                  <li>Once your booking is confirmed, the amount paid is not refunded if you cancel.</li>
                  <li>This applies to cancellations made at any time before check-in, as well as no-shows and early check-outs.</li>
                  <li>Unused hours or nights are not refunded.</li>
                </ul>
              </Block>
              <Block icon={ClipboardCheck} title="Your acknowledgement">
                <p>Before you can confirm a booking you must tick a box confirming that you understand it is non-refundable. We record your acceptance with the booking.</p>
                <p>Please check your dates, guests and timings carefully on the review screen before confirming.</p>
              </Block>
            </>
          ) : (
            <Block icon={ClipboardCheck} title="Refund schedule">
              <ul className="list-disc pl-5 space-y-2">{c.lines.map((l) => <li key={l}>{l}</li>)}</ul>
            </Block>
          )}

          <Block icon={CalendarX} title="If you can't make it">
            <p>You can still cancel from <Link to="/dashboard" className="text-gold underline">My bookings</Link> so the dates are released, but no refund will be issued{nonRefundable ? "" : " beyond what the schedule above allows"}.</p>
            <p>Check-in is from {t.check_in_label} and check-out is before {t.check_out_label}. A stay that has already started cannot be cancelled online.</p>
          </Block>

          <Block icon={ShieldAlert} title="If we have to cancel">
            <p>If the property becomes unavailable because of us, we will contact you straight away to offer alternative dates or a full refund of anything you have paid.</p>
          </Block>

          <Block icon={MessageCircle} title="Questions?">
            <p>Talk to us before you book if you're unsure.</p>
            {data.contact_phone && <p><a href={`tel:${data.contact_phone}`} className="text-gold">{data.contact_phone}</a></p>}
            {data.contact_email && <p><a href={`mailto:${data.contact_email}`} className="text-gold">{data.contact_email}</a></p>}
          </Block>
        </div>

        <div className="text-center mt-12">
          <Link to="/booking" className="btn-gold inline-flex items-center gap-2">Check availability <ArrowRight size={14} /></Link>
          <p className="text-white/30 text-xs mt-4">See also the <Link to="/rules" className="text-gold">house rules</Link> and <Link to="/policies" className="text-gold">full policies</Link>.</p>
        </div>
      </div>
    </div>
  );
}
